<script setup>
import {onMounted, ref, watch} from 'vue'
import {useTimeControlsStore} from "../stores/TimeControls";
import {storeToRefs} from "pinia";

const props = defineProps({
    timeType: {
        type: String,
        required: true
    },
    open: {
        type: [String, Boolean],
        required: false
    },
    close: {
        type: [String, Boolean],
        required: false
    },
    icon: {
        type: [String, Boolean],
        required: false
    },
    time: {
        type: [String, Boolean],
        required: false
    },
    timezone: {
        type: [String, Boolean],
        required: false
    },
    additionalText: {
        type: [String, Boolean],
        required: false
    }
})

const timeControls = useTimeControlsStore(),
    { timeDynamic } = storeToRefs(timeControls),
    { timeInTimezone } = timeControls

const openHours = ref(9),
      closeHours = ref(18),
      currentTime = ref(null)

onMounted(() => {
    if (props.timeType === 'open_hours') {
        currentTime.value = timeInTimezone(props.timezone)
        openHours.value = parseInt(props.open)
        closeHours.value = parseInt(props.close)
    }
})

watch(timeDynamic, () => {
    if (props.timeType === 'open_hours') {
        currentTime.value = timeInTimezone(props.timezone)
    }
})
</script>

<template>
    <div v-if="currentTime || props.timeType === 'static_time'">
        <div v-if="timeType !== 'static_time'">
            <div class="w-full flex gap-8">
                <div v-if="currentTime.hours() < closeHours && currentTime.hours() >= openHours" class="icon-container w-8 h-8">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><!--! Font Awesome Pro 6.4.0 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license (Commercial License) Copyright 2023 Fonticons, Inc. --><path d="M256 0c8.8 0 16 7.2 16 16V96c0 8.8-7.2 16-16 16s-16-7.2-16-16V16c0-8.8 7.2-16 16-16zM0 256c0-8.8 7.2-16 16-16H96c8.8 0 16 7.2 16 16s-7.2 16-16 16H16c-8.8 0-16-7.2-16-16zm400 0c0-8.8 7.2-16 16-16h80c8.8 0 16 7.2 16 16s-7.2 16-16 16H416c-8.8 0-16-7.2-16-16zM256 400c8.8 0 16 7.2 16 16v80c0 8.8-7.2 16-16 16s-16-7.2-16-16V416c0-8.8 7.2-16 16-16zM75 75c6.2-6.2 16.4-6.2 22.6 0l56.6 56.6c6.2 6.2 6.2 16.4 0 22.6s-16.4 6.2-22.6 0L75 97.6c-6.2-6.2-6.2-16.4 0-22.6zm0 362c-6.2-6.2-6.2-16.4 0-22.6l56.6-56.6c6.2-6.2 16.4-6.2 22.6 0s6.2 16.4 0 22.6L97.6 437c-6.2 6.2-16.4 6.2-22.6 0zM357.8 154.2c-6.2-6.2-6.2-16.4 0-22.6L414.4 75c6.2-6.2 16.4-6.2 22.6 0s6.2 16.4 0 22.6l-56.6 56.6c-6.2 6.2-16.4 6.2-22.6 0zm0 203.6c6.2-6.2 16.4-6.2 22.6 0L437 414.4c6.2 6.2 6.2 16.4 0 22.6s-16.4 6.2-22.6 0l-56.6-56.6c-6.2-6.2-6.2-16.4 0-22.6zM336 256a80 80 0 1 0 -160 0 80 80 0 1 0 160 0zm-192 0a112 112 0 1 1 224 0 112 112 0 1 1 -224 0z"/></svg>
                </div>
                <div v-else class="icon-container w-8 h-8">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><!--! Font Awesome Pro 6.4.0 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license (Commercial License) Copyright 2023 Fonticons, Inc. --><path d="M304 0c8.8 0 16 7.2 16 16V48h32c8.8 0 16 7.2 16 16s-7.2 16-16 16H320v32c0 8.8-7.2 16-16 16s-16-7.2-16-16V80H256c-8.8 0-16-7.2-16-16s7.2-16 16-16h32V16c0-8.8 7.2-16 16-16zM156.6 163.9C85.3 180 32 243.8 32 320.2c0 88.5 71.6 160.2 159.8 160.2c25.7 0 50-6.1 71.5-16.9c-87.6-11.3-155.1-86.4-155.1-177.2c0-47.4 18.4-90.4 48.4-122.4zM0 320.2C0 214.1 85.8 128 191.8 128c5.5 0 10.9 .2 16.3 .7c7 .6 12.8 5.7 14.3 12.5s-1.6 13.9-7.7 17.3c-44.4 25.2-74.4 73-74.4 127.8c0 81 65.5 146.6 146.2 146.6c8.6 0 17-.7 25.1-2.2c6.9-1.2 13.8 2.2 17 8.5s1.9 13.8-3.1 18.7c-34.5 33.6-81.7 54.4-133.6 54.4C85.8 512.4 0 426.3 0 320.2zM432 160v64h64c8.8 0 16 7.2 16 16s-7.2 16-16 16H432v64c0 8.8-7.2 16-16 16s-16-7.2-16-16V256H336c-8.8 0-16-7.2-16-16s7.2-16 16-16h64V160c0-8.8 7.2-16 16-16s16 7.2 16 16z"/></svg>
                </div>
                <div>
                    <div>
                        <span class="inline-block font-bold">
                            {{ currentTime.format('HH') }}<span class="cintra-animate-time">:</span>{{ currentTime.format('mm') }}
                        </span>
                        <span class="inline-block pl-2">({{ props.timezone }})</span>
                    </div>

                    <div class="w-full">
                        <span>{{ props.open }} - {{ props.close }}</span>
                        <div v-if="props.additionalText" v-html="props.additionalText" />
                    </div>
                </div>
            </div>
        </div>

        <div v-else class="w-full flex gap-8">
            <div class="icon-container w-8 h-8" v-html="props.icon" />
            <div>
                <span class="inline-block font-bold">
                    {{ props.time }}
                </span>

                <div v-if="props.additionalText" v-html="props.additionalText" />
            </div>
        </div>
    </div>
</template>

<style>
    .cintra-animate-time {
        animation-name: cintra-animate-time;
        animation-duration: 1s;
        animation-iteration-count: infinite;
        animation-timing-function: ease-in-out;
    }

    @keyframes cintra-animate-time {
        0% {
            @apply opacity-0
        }
        50% {
            @apply opacity-100
        }
        100% {
            @apply opacity-0
        }
    }
</style>
